import { TERMINAL_TOOL_NAMES } from './external-tool.types';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
  type TerraUIMessagePart,
} from './messages.types';
import type {
  ToolCallAudience,
  ToolCallGenericMessage,
} from './toolcalls.types';
import {
  redactUserVisibleInternalPaths,
  stripTorchFileReference,
} from './torch-file-reference';

export const AGENT_INTERNAL_AUDIENCE: ToolCallAudience = 'agent-internal';

const TOOL_CALL_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  DataMessageTypeEnum.ToolCallStart,
  DataMessageTypeEnum.ToolCallUpdate,
  DataMessageTypeEnum.ToolCallComplete,
  DataMessageTypeEnum.ToolCallError,
]);

const USER_VISIBLE_SHELL_TOOLS: ReadonlySet<string> = new Set(
  TERMINAL_TOOL_NAMES,
);

const TORCH_FILESYSTEM_REFERENCE_PATTERN =
  /(?:^|[\s("'`])\/(?:workspace|artifacts|skills|\.subagent-skills|custom-skills|testing-instructions|memories)\/|Full results saved to:|virtual filesystem/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isToolCallMessage = (value: unknown): value is ToolCallGenericMessage =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  TOOL_CALL_MESSAGE_TYPES.has(value.type) &&
  typeof value.toolCallId === 'string';

const stripTorchFileReferencesDeep = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return stripTorchFileReference(value);
  }

  if (Array.isArray(value)) {
    return value.map(stripTorchFileReferencesDeep);
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        stripTorchFileReferencesDeep(entry),
      ]),
    );
  }

  return value;
};

const SHELL_TARGET_FIELDS = ['command', 'cwd'] as const;

const shellTargetReadsInternalFilesystem = (input: unknown): boolean => {
  if (!isRecord(input)) {
    return false;
  }

  return SHELL_TARGET_FIELDS.some((field) => {
    const value = input[field];

    return (
      typeof value === 'string' &&
      value !== redactUserVisibleInternalPaths(value)
    );
  });
};

const shellInputTargetsInternalFilesystem = (
  message: ToolCallGenericMessage,
): boolean =>
  USER_VISIBLE_SHELL_TOOLS.has(message.toolName) &&
  shellTargetReadsInternalFilesystem(message.input);

export function projectUserVisibleToolCall(
  message: ToolCallGenericMessage,
): ToolCallGenericMessage | null {
  if (
    message.audience === AGENT_INTERNAL_AUDIENCE ||
    shellInputTargetsInternalFilesystem(message)
  ) {
    return null;
  }

  return {
    ...message,
    ...(typeof message.message === 'string'
      ? { message: stripTorchFileReference(message.message) }
      : {}),
    ...(typeof message.error === 'string'
      ? { error: stripTorchFileReference(message.error) }
      : {}),
    ...(message.input === undefined
      ? {}
      : { input: stripTorchFileReferencesDeep(message.input) }),
    ...(message.output === undefined
      ? {}
      : { output: stripTorchFileReferencesDeep(message.output) }),
  };
}

export function projectUserVisibleDataPart(
  data: unknown,
): { data: unknown } | null {
  if (!isToolCallMessage(data)) {
    return { data };
  }

  const projected = projectUserVisibleToolCall(data);

  return projected ? { data: projected } : null;
}

export function projectUserVisibleMessages(
  messages: readonly TerraUIMessage[],
): TerraUIMessage[] {
  return messages.map((message) => ({
    ...message,
    parts: message.parts.flatMap<TerraUIMessagePart>((part) => {
      if (
        message.role !== 'user' &&
        (part.type === 'text' || part.type === 'reasoning')
      ) {
        return [
          {
            ...part,
            text: redactUserVisibleInternalPaths(part.text),
          },
        ];
      }

      if (
        part.type !== TerraUIMessageDataPartName ||
        !isToolCallMessage(part.data)
      ) {
        return [part];
      }

      const projected = projectUserVisibleToolCall(part.data);

      return projected ? [{ ...part, data: projected }] : [];
    }),
  }));
}

export function containsTorchFilesystemReference(text: string): boolean {
  return TORCH_FILESYSTEM_REFERENCE_PATTERN.test(text);
}
